import { BR_ONLY, isOldBlankLine } from '@src/components/sco/MarkdownRenderer/remarkOldBlankLines'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkStringify from 'remark-stringify'
import { Node as SlateNode } from 'slate'
import { unified } from 'unified'

/**
 * Markdown ⇄ Slate for `RichMarkdownInput`. Both directions go through the same mdast (remark
 * + GFM), so the editor reads the markdown it writes, and what it writes renders like the bubble.
 * The dialect is in project-plans/active/2026-10-30-markdown-editor-parity (§4):
 *
 * - Paragraphs are separated by a blank line; a `\n` inside a paragraph is a line break.
 * - Empty paragraphs are not written (L2). An old `<br>` blank line reads as a paragraph break.
 * - Marks: `**bold**`, `_italic_`, `~~strikethrough~~`, `<u>underline</u>`, `` `code` ``.
 * - Mentions are `[@Label](mention:…)` links.
 * - Anything the editor can't edit (HTML, `sco-*` embeds, tables, …) becomes a `raw_markdown`
 *   node holding its source, and is written back byte for byte (L7).
 */

const {
  BLOCK_QUOTE,
  CODE_BLOCK,
  IMAGE,
  LINK,
  LIST_ITEM,
  LIST_ITEM_TEXT,
  MENTION,
  OL_LIST,
  PARAGRAPH,
  RAW_MARKDOWN,
  THEMATIC_BREAK,
  UL_LIST,
} = EditorElementTypes

const HEADINGS = [
  EditorElementTypes.HEADING_ONE,
  EditorElementTypes.HEADING_TWO,
  EditorElementTypes.HEADING_THREE,
  EditorElementTypes.HEADING_FOUR,
  EditorElementTypes.HEADING_FIVE,
  EditorElementTypes.HEADING_SIX,
]

const parser = unified().use(remarkParse).use(remarkGfm)

const stringifier = unified().use(remarkGfm).use(remarkStringify, {
  bullet: '-',
  emphasis: '_',
  fences: true,
  incrementListMarker: true,
  listItemIndent: 'one',
  rule: '-',
  strong: '*',
})

const isText = (node: any) => typeof node?.text === 'string' && !node.type
const emptyParagraph = () => ({ type: PARAGRAPH, children: [{ text: '' }] })
const rawBlock = (source: string) => ({ type: RAW_MARKDOWN, source, children: [{ text: '' }] })
const rawInline = (source: string) => ({ type: RAW_MARKDOWN, inline: true, source, children: [{ text: '' }] })

// ─── markdown → Slate ────────────────────────────────────────────────────────────────────────

const OPEN_TAG = /^<([a-z][\w-]*)(\s[^>]*)?>$/i

const sliceOf = (source: string, from: any, to: any = from) =>
  source.slice(from.position.start.offset, to.position.end.offset)

const plainText = (node: any): string =>
  typeof node.value === 'string' ? node.value : (node.children || []).map(plainText).join('')

/** Index of the `</name>` that closes `children[from]`, at the same level, or -1. */
const findClosingTag = (children: any[], from: number, name: string) => {
  const open = new RegExp(`^<${name}(\\s[^>]*)?>$`, 'i')
  const close = new RegExp(`^</${name}\\s*>$`, 'i')
  let depth = 0
  for (let j = from + 1; j < children.length; j++) {
    if (children[j].type !== 'html') {
      continue
    }
    if (open.test(children[j].value)) {
      depth++
    } else if (close.test(children[j].value)) {
      if (depth === 0) {
        return j
      }
      depth--
    }
  }
  return -1
}

/**
 * Rewrites phrasing content before conversion: `<u>…</u>` becomes an `underline` container, any
 * other paired tag (`<kbd>…</kbd>`, `<sco-citation>…</sco-citation>`) one atomic raw run, an old
 * `<br>` blank line a `blankLine` marker, and inline content the editor can't hold raw source.
 */
const groupPhrasing = (children: any[], source: string): any[] => {
  const out = []
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    if (child.type === 'html') {
      if (isOldBlankLine(children, i)) {
        out.push({ type: 'blankLine' })
        continue
      }
      const open = child.value.match(OPEN_TAG)
      const close = open && !child.value.endsWith('/>') ? findClosingTag(children, i, open[1]) : -1
      if (close !== -1 && open[1].toLowerCase() === 'u') {
        out.push({ type: 'underline', children: groupPhrasing(children.slice(i + 1, close), source) })
        i = close
      } else if (close !== -1) {
        out.push({ type: 'rawInline', value: sliceOf(source, child, children[close]) })
        i = close
      } else {
        out.push({ type: 'rawInline', value: child.value })
      }
      continue
    }
    if (['emphasis', 'strong', 'delete', 'link'].includes(child.type)) {
      out.push({ ...child, children: groupPhrasing(child.children, source) })
      continue
    }
    if (['text', 'inlineCode', 'break'].includes(child.type)) {
      out.push(child)
      continue
    }
    // Images inside text, references, footnotes, math: kept as source
    out.push({ type: 'rawInline', value: child.position ? sliceOf(source, child) : plainText(child) })
  }
  return out
}

const MARK_OF = { emphasis: 'italic', strong: 'bold', delete: 'strikethrough', underline: 'underline' }

/** Slate wants text at both ends of a block and between two inline elements. */
const padInlines = (nodes: any[]) => {
  const out = []
  for (const node of nodes) {
    const last = out[out.length - 1]
    if (!isText(node) && (!last || !isText(last))) {
      out.push({ text: '' })
    }
    out.push(node)
  }
  if (!out.length || !isText(out[out.length - 1])) {
    out.push({ text: '' })
  }
  return out
}

const inlinesFrom = (phrasing: any[], marks: Record<string, boolean> = {}): any[] => {
  const out = []
  for (const node of phrasing) {
    switch (node.type) {
      case 'text':
        out.push({ text: node.value, ...marks })
        break
      case 'break':
      case 'blankLine':
        out.push({ text: '\n', ...marks })
        break
      case 'inlineCode':
        out.push({ text: node.value, ...marks, code: true })
        break
      case 'emphasis':
      case 'strong':
      case 'delete':
      case 'underline':
        out.push(...inlinesFrom(node.children, { ...marks, [MARK_OF[node.type]]: true }))
        break
      case 'link':
        if (node.url?.startsWith('mention:')) {
          out.push({
            type: MENTION,
            mentionable: { identifier: node.url, label: plainText(node).replace(/^@/, '') },
            children: [{ text: '' }],
          })
        } else {
          out.push({
            type: LINK,
            url: node.url,
            ...(node.title ? { title: node.title } : {}),
            children: padInlines(inlinesFrom(node.children, marks)),
          })
        }
        break
      case 'rawInline':
        out.push(rawInline(node.value))
        break
    }
  }
  return out
}

const inlineChildren = (phrasing: any[]) => padInlines(inlinesFrom(phrasing))

const trimEdgeNewline = (segment: any[]) => {
  const out = [...segment]
  if (out[0]?.type === 'text') {
    out[0] = { ...out[0], value: out[0].value.replace(/^\n/, '') }
  }
  const last = out.length - 1
  if (out[last]?.type === 'text') {
    out[last] = { ...out[last], value: out[last].value.replace(/\n$/, '') }
  }
  return out
}

const isBlankPhrasing = (phrasing: any[]) => phrasing.every(n => n.type === 'text' && !n.value.trim())

const paragraphBlocks = (node: any, source: string): any[] => {
  const children = node.children
  if (
    children.some(c => c.type === 'image') &&
    children.every(c => c.type === 'image' || isBlankPhrasing([c]))
  ) {
    return children
      .filter(c => c.type === 'image')
      .map(c => ({ type: IMAGE, link: c.url, caption: c.alt ?? '', children: [{ text: '' }] }))
  }

  const grouped = groupPhrasing(children, source)
  const content = grouped.filter(n => !(n.type === 'text' && !n.value.trim()))
  if (content.length === 1 && content[0].type === 'rawInline') {
    // A paragraph that is only an embed (e.g. `<sco-question>…</sco-question>`) is a block
    return [rawBlock(sliceOf(source, node))]
  }

  // Old `<br>` blank lines split the paragraph; empty pieces are dropped (L2)
  const segments: any[][] = [[]]
  for (const n of grouped) {
    if (n.type === 'blankLine') {
      segments.push([])
    } else {
      segments[segments.length - 1].push(n)
    }
  }
  return segments
    .map(trimEdgeNewline)
    .filter(segment => !isBlankPhrasing(segment))
    .map(segment => ({ type: PARAGRAPH, children: inlineChildren(segment) }))
}

const listFrom = (list: any, source: string): any => {
  const items = []
  for (const listItem of list.children) {
    if (listItem.checked !== null && listItem.checked !== undefined) {
      return null // task lists stay raw
    }
    const [first, ...rest] = listItem.children
    const textPhrasing = first?.type === 'paragraph' ? groupPhrasing(first.children, source) : []
    const nested = []
    for (const child of first?.type === 'paragraph' ? rest : listItem.children) {
      const nestedList = child.type === 'list' ? listFrom(child, source) : null
      if (!nestedList) {
        return null // a second paragraph, code, a quote: the list stays raw
      }
      nested.push(nestedList)
    }
    items.push({
      type: LIST_ITEM,
      children: [{ type: LIST_ITEM_TEXT, children: inlineChildren(textPhrasing) }, ...nested],
    })
  }
  return {
    type: list.ordered ? OL_LIST : UL_LIST,
    ...(list.ordered && typeof list.start === 'number' && list.start !== 1 ? { start: list.start } : {}),
    ...(list.spread ? { spread: true } : {}),
    children: items,
  }
}

const blocksFrom = (node: any, source: string): any[] => {
  switch (node.type) {
    case 'paragraph':
      return paragraphBlocks(node, source)
    case 'heading':
      return [
        { type: HEADINGS[node.depth - 1], children: inlineChildren(groupPhrasing(node.children, source)) },
      ]
    case 'blockquote':
      if (node.children.length && node.children.every(c => c.type === 'paragraph')) {
        const paragraphs = node.children.flatMap(c => paragraphBlocks(c, source))
        return [
          {
            type: BLOCK_QUOTE,
            children:
              paragraphs.length === 1 && paragraphs[0].type === PARAGRAPH
                ? paragraphs[0].children
                : paragraphs,
          },
        ]
      }
      return [rawBlock(sliceOf(source, node))]
    case 'list':
      return [listFrom(node, source) ?? rawBlock(sliceOf(source, node))]
    case 'code':
      return [
        {
          type: CODE_BLOCK,
          ...(node.lang ? { language: node.lang } : {}),
          ...(node.meta ? { meta: node.meta } : {}),
          children: [{ text: node.value }],
        },
      ]
    case 'thematicBreak':
      return [{ type: THEMATIC_BREAK, children: [{ text: '' }] }]
    case 'html':
      return BR_ONLY.test(node.value) ? [] : [rawBlock(node.value)]
    default:
      // Tables, definitions, footnotes, math: kept as source (L7)
      return [rawBlock(sliceOf(source, node))]
  }
}

/**
 * Deserialize a list of nodes from markdownString
 * @param markdownString
 */
export const deserializeNodesFromMarkdown = (markdownString: any): SlateNode[] => {
  const source = typeof markdownString === 'string' ? markdownString : ''
  try {
    const value = parser.parse(source).children.flatMap(node => blocksFrom(node, source))
    return value.length ? value : [emptyParagraph()]
  } catch (err) {
    console.log(err)
    // Never lose the body: keep it whole as source the editor can't change
    return source ? [rawBlock(source)] : [emptyParagraph()]
  }
}

// ─── Slate → markdown ────────────────────────────────────────────────────────────────────────

const MARK_ORDER = ['bold', 'italic', 'strikethrough', 'underline']

const hasMark = (node: any, mark: string) => isText(node) && node[mark] === true

/** Moves whitespace at the edges of a marked run outside it: `** bold **` is not bold. */
const peelWhitespace = (children: any[]) => {
  const before = []
  const after = []
  const inner = [...children]
  const first = inner[0]
  if (first?.type === 'text') {
    const [, space, rest] = first.value.match(/^(\s*)([\s\S]*)$/)
    if (space) {
      before.push({ type: 'text', value: space })
      inner[0] = { ...first, value: rest }
    }
  }
  const last = inner[inner.length - 1]
  if (last?.type === 'text') {
    const [, rest, space] = last.value.match(/^([\s\S]*?)(\s*)$/)
    if (space) {
      after.push({ type: 'text', value: space })
      inner[inner.length - 1] = { ...last, value: rest }
    }
  }
  return { before, inner: inner.filter(n => !(n.type === 'text' && n.value === '')), after }
}

const wrapInMark = (mark: string, children: any[]): any[] => {
  const { before, inner, after } = peelWhitespace(children)
  if (!inner.length) {
    return [...before, ...after]
  }
  const wrapped =
    mark === 'underline'
      ? [{ type: 'html', value: '<u>' }, ...inner, { type: 'html', value: '</u>' }]
      : [{ type: { bold: 'strong', italic: 'emphasis', strikethrough: 'delete' }[mark], children: inner }]
  return [...before, ...wrapped, ...after]
}

const phrasingOf = (node: any): any[] => {
  if (isText(node)) {
    return [node.code ? { type: 'inlineCode', value: node.text } : { type: 'text', value: node.text }]
  }
  switch (node.type) {
    case LINK:
      // A link whose text is its URL is written `<url>`: a CommonMark autolink, so readers without
      // GFM (s3 Feed, emails) still link it, which is why `linkifyNodes` runs before sending
      return [
        {
          type: 'link',
          url: node.url ?? node.link ?? '',
          title: node.title ?? null,
          children: phrasingFrom(node.children),
        },
      ]
    case MENTION:
      return [
        {
          type: 'link',
          url: node.mentionable?.identifier ?? '',
          title: null,
          children: [{ type: 'text', value: `@${node.mentionable?.label ?? ''}` }],
        },
      ]
    case RAW_MARKDOWN:
      return [{ type: 'html', value: node.source ?? '' }]
    default:
      return phrasingFrom(node.children || [])
  }
}

/** Groups runs of leaves that share a mark into one mdast mark node, outermost mark first. */
const nestMarks = (nodes: any[], marks: string[]): any[] => {
  if (!marks.length) {
    return nodes.flatMap(phrasingOf)
  }
  const [mark, ...rest] = marks
  const out = []
  let i = 0
  while (i < nodes.length) {
    const marked = hasMark(nodes[i], mark)
    let j = i
    while (j < nodes.length && hasMark(nodes[j], mark) === marked) {
      j++
    }
    const run = nodes.slice(i, j)
    if (marked) {
      out.push(
        ...wrapInMark(
          mark,
          nestMarks(
            run.map(n => ({ ...n, [mark]: false })),
            marks
          )
        )
      )
    } else {
      out.push(...nestMarks(run, rest))
    }
    i = j
  }
  return out
}

const phrasingFrom = (children: any[]) =>
  nestMarks(
    children.filter(c => !(isText(c) && c.text === '')),
    MARK_ORDER
  )

const isBlank = (phrasing: any[]) => phrasing.every(n => n.type === 'text' && !n.value.trim())

const isBlockElement = (node: any) =>
  !isText(node) && node.type !== LINK && node.type !== MENTION && !node.inline

/** List items hold their text in `list_item_text`, a paragraph inside it, or directly (toolbar lists). */
const itemInlines = (children: any[]): any[] =>
  children.flatMap(c =>
    c.type === LIST_ITEM_TEXT || c.type === PARAGRAPH
      ? itemInlines(c.children)
      : c.type === UL_LIST || c.type === OL_LIST
        ? []
        : [c]
  )

const listToMdast = (node: any): any => {
  const items = node.children
    .map(item => {
      const children = item.type === LIST_ITEM ? item.children : [item]
      const phrasing = phrasingFrom(itemInlines(children))
      const nested = children
        .filter(c => c.type === UL_LIST || c.type === OL_LIST)
        .map(listToMdast)
        .filter(Boolean)
      if (isBlank(phrasing) && !nested.length) {
        return null
      }
      return {
        type: 'listItem',
        spread: false,
        checked: null,
        children: [{ type: 'paragraph', children: phrasing }, ...nested],
      }
    })
    .filter(Boolean)
  if (!items.length) {
    return null
  }
  const ordered = node.type === OL_LIST
  return {
    type: 'list',
    ordered,
    start: ordered ? (node.start ?? 1) : null,
    spread: !!node.spread,
    children: items,
  }
}

const blockToMdast = (node: any): any => {
  if (isText(node)) {
    return blockToMdast({ type: PARAGRAPH, children: [node] })
  }
  const headingIndex = HEADINGS.indexOf(node.type)
  if (headingIndex !== -1) {
    const phrasing = phrasingFrom(node.children)
    return isBlank(phrasing) ? null : { type: 'heading', depth: headingIndex + 1, children: phrasing }
  }
  switch (node.type) {
    case BLOCK_QUOTE: {
      const children = node.children.some(isBlockElement)
        ? node.children.map(blockToMdast).filter(Boolean)
        : [blockToMdast({ type: PARAGRAPH, children: node.children })].filter(Boolean)
      return children.length ? { type: 'blockquote', children } : null
    }
    case UL_LIST:
    case OL_LIST:
      return listToMdast(node)
    case LIST_ITEM:
      return listToMdast({ type: UL_LIST, children: [node] })
    case CODE_BLOCK: {
      const value = (node.children || []).map(c => c.text ?? '').join('')
      return value ? { type: 'code', lang: node.language || null, meta: node.meta || null, value } : null
    }
    case THEMATIC_BREAK:
      return { type: 'thematicBreak' }
    case IMAGE:
      return {
        type: 'paragraph',
        children: [{ type: 'image', url: node.link ?? node.url ?? '', alt: node.caption ?? '' }],
      }
    case RAW_MARKDOWN:
      return { type: 'html', value: node.source ?? '' }
    default: {
      // Paragraphs, list_item_text, and any unknown block: their inline content
      const phrasing = phrasingFrom(itemInlines(node.children || []))
      return isBlank(phrasing) ? null : { type: 'paragraph', children: phrasing }
    }
  }
}

export const serializeNodesToMarkdown = (nodes: any): string => {
  const blocks = (Array.isArray(nodes) ? nodes : []).map(blockToMdast).filter(Boolean)
  try {
    return stringifier.stringify({ type: 'root', children: blocks } as any).replace(/\n+$/, '')
  } catch (err) {
    console.log(err)
    // Never send an error string as a body: fall back to the plain text
    return (Array.isArray(nodes) ? nodes : [])
      .map(n => SlateNodeString(n))
      .filter(Boolean)
      .join('\n\n')
  }
}

const SlateNodeString = (node: any): string =>
  isText(node)
    ? node.text
    : node.type === RAW_MARKDOWN
      ? (node.source ?? '')
      : (node.children || []).map(SlateNodeString).join('')
